import { RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { RestoreOriginal } from "./SaveControls";
import { useDeck } from "../../lib/deck-context";
import { BASE, fmtM, fmtPct } from "../../lib/model";
import { Eyebrow } from "./primitives";

const GROUPS = [
  { title: "Revenue + Operations", fields: [
    ["sf", "Rentable area", "SF", 1000], ["rent", "Rent per SF / yr", "$", 0.5], ["occ", "Stabilized occupancy", "%", 0.5],
    ["other", "Other revenue / yr", "$", 10000], ["exp", "Expense / reserve load", "%", 0.5], ["groundRent", "City ground rent / yr", "$", 10000],
  ]},
  { title: "Valuation + Exit", fields: [
    ["cap", "Capitalization rate", "%", 0.05], ["saleCost", "Sale cost", "%", 0.25], ["ltv", "Refinance loan-to-value", "%", 1], ["refiCost", "Refinance cost", "%", 0.25],
  ]},
  { title: "Cost + Capital", fields: [
    ["tdc", "Total Development Cost", "$", 50000], ["debtPct", "Construction debt", "%", 1], ["equityPct", "Equity", "%", 1],
  ]},
  { title: "Site Plan", fields: [
    ["parkingStalls", "Parking spaces", "#", 5], ["courtyardSf", "Courtyard / open space", "SF", 5000],
  ]},
  { title: "JV Split + Timeline", fields: [
    ["partnerCapPct", "Partner share of cash invested", "%", 5], ["prefRate", "Preferred return (annual)", "%", 0.5], ["jvSplit", "Arqitek share after pref", "%", 5], ["mControl", "Months to development control", "mo", 1], ["mPad", "Months to pad-ready", "mo", 1],
    ["mBuildReady", "Months to build-ready", "mo", 1], ["mSell", "Months to build + sell", "mo", 1], ["mHold", "Months to build + hold refi", "mo", 1],
  ]},
];

const Field = ({ k, label, unit, step }) => {
  const { raw: inputs, setInput } = useDeck();
  const changed = inputs[k] !== BASE[k];
  return (
    <label className="flex items-center justify-between gap-4 py-2.5 border-b border-line/70 last:border-0">
      <span className="text-sm text-char flex items-center gap-2">
        {label}
        {changed && <span className="h-1.5 w-1.5 rounded-full bg-arq" />}
      </span>
      <span className="flex items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1.5 focus-within:border-arq transition-colors">
        {unit === "$" && <span className="text-xs text-steel">$</span>}
        <input
          data-testid={`assumption-input-${k}`}
          type="number"
          step={step}
          value={inputs[k]}
          onChange={(e) => setInput(k, e.target.value === "" ? 0 : Number(e.target.value))}
          className="w-[104px] text-right text-sm tabular-nums text-ink bg-transparent outline-none"
        />
        {unit !== "$" && <span className="text-xs text-steel w-5">{unit}</span>}
      </span>
    </label>
  );
};

const PredevNote = () => {
  const { inputs: a } = useDeck();
  return (
    <div data-testid="panel-predev-note" className="rounded-xl border border-line bg-white px-4 py-3 text-xs text-steel leading-relaxed">
      Stage costs (cumulative): control {fmtM(a.controlCost)} · pad-ready {fmtM(a.padCost)} · build-ready {fmtM(a.buildReadyCost)}. Edit line items on the Exit Strategy page.
      {a.predevDelta !== 0 && <div className="mt-1 text-arq">Line-item changes adjust TDC by {a.predevDelta > 0 ? "+" : "−"}{fmtM(Math.abs(a.predevDelta))} → {fmtM(a.tdc)}.</div>}
    </div>
  );
};

const Output = ({ label, value, testid }) => (
  <div>
    <div className="text-[10px] font-mono tracking-[0.16em] text-white/45 uppercase">{label}</div>
    <div data-testid={testid} className="mt-1 font-display text-2xl tabular-nums">{value}</div>
  </div>
);

export const AssumptionsPanel = () => {
  const { panelOpen, setPanelOpen, m, reset, modified } = useDeck();
  return (
    <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
      <SheetContent data-testid="assumptions-panel" side="right" className="w-full sm:max-w-[460px] p-0 overflow-y-auto bg-paper border-l border-line">
        <div className="bg-ink-deep text-white px-7 pt-8 pb-7">
          <SheetHeader className="text-left space-y-1">
            <Eyebrow className="!text-arq-mid">Interactive Underwriting</Eyebrow>
            <SheetTitle className="font-display text-3xl font-normal text-white">Assumptions</SheetTitle>
            <SheetDescription className="text-white/55 text-sm">Session only. Every section recalculates live.</SheetDescription>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-5 mt-7">
            <Output label="NOI" value={fmtM(m.noi, 3)} testid="panel-output-noi" />
            <Output label="Stabilized value" value={fmtM(m.value)} testid="panel-output-value" />
            <Output label="Build + Sell profit" value={fmtM(m.profit)} testid="panel-output-profit" />
            <Output label="Profit on cost" value={fmtPct(m.poc * 100, 1)} testid="panel-output-poc" />
          </div>
        </div>
        <div className="px-7 py-6 space-y-7">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <Eyebrow className="mb-2">{g.title}</Eyebrow>
              <div className="rounded-xl border border-line bg-white px-4">
                {g.fields.map(([k, label, unit, step]) => <Field key={k} k={k} label={label} unit={unit} step={step} />)}
              </div>
            </div>
          ))}
          <PredevNote />
          <button data-testid="assumptions-reset-btn" disabled={!modified} onClick={reset} className="w-full flex items-center justify-center gap-2 rounded-full bg-ink text-white py-3 text-sm hover:bg-ink-deep transition-colors disabled:opacity-40">
            <RotateCcw size={14} /> Undo Unsaved Changes
          </button>
          <RestoreOriginal />
          <p className="text-xs text-steel leading-relaxed">Buyer profit reserves for early-stage residual values: 35% / 30% / 25% of remaining cost. Modeled exit friction: $25K / $50K / $75K.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
